﻿import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Download,
  ExternalLink,
  History,
  Link2,
  Plus,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@foundation/components/ui/button";
import { useToast } from "@foundation/hooks/use-toast";
import { useUser } from "@foundation/contextapi/UserContext";
import { getActiveWorkspaceId } from "../../../header/activeWorkspace";
import { submitDocumentRegisterSignup } from "../services/documentRegisterSignupService";
import RevisrLayout from "../components/RevisrLayout";

const REGISTER_ROWS = [
  { type: "01 Manual",    title: "IMS Manual",                                     date: "9/7/2024",  cls: "business", link: "IMS Manual" },
  { type: "01 Manual",    title: "Employee Handbook 2023",                          date: "1/2/2023",  cls: "business", link: "Employee Handbook 2023" },
  { type: "02 Policy",    title: "Work Health & Safety Policy",                     date: "6/17/2024", cls: "public",   link: "Work Health & Safety Policy" },
  { type: "02 Policy",    title: "Performance Counselling and Discipline Policy",   date: "8/15/2024", cls: "public",   link: "Performance Counselling and Discipline Policy" },
  { type: "02 Policy",    title: "Code of Conduct Policy",                          date: "8/15/2024", cls: "public",   link: "Code of Conduct Policy" },
  { type: "02 Policy",    title: "Workplace Surveillance Policy",                   date: "8/15/2024", cls: "public",   link: "Workplace Surveillance Policy" },
  { type: "02 Policy",    title: "Psychological Health & Wellbeing Policy",         date: "6/17/2024", cls: "public",   link: "Psychological Health & Wellbeing Policy" },
  { type: "02 Policy",    title: "Quality Policy",                                  date: "3/20/2024", cls: "public",   link: "Quality Policy" },
  { type: "02 Policy",    title: "WHS Responsibility Statement",                    date: "6/16/2024", cls: "public",   link: "WHS Responsibility Statement" },
  { type: "03 Procedure", title: "Procedure for Accounts Payable",                  date: "3/20/2024", cls: "business", link: "Procedure for Accounts Payable" },
  { type: "03 Procedure", title: "Procedure for Business Management and Reporting", date: "3/20/2024", cls: "business", link: "Procedure for Business Management and Reporting" },
  { type: "03 Procedure", title: "Procedure for Control of Documented Information", date: "3/20/2024", cls: "business", link: "Procedure for Control of Documented Information" },
];

export const DocumentRegister = () => {
  const { toast } = useToast();
  const { user } = useUser();
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  // Prefill with the account email once the user loads; keep any manual edits.
  useEffect(() => {
    if (!emailTouched && user?.email) setEmail(user.email);
  }, [user?.email, emailTouched]);

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      toast({ title: "Sign in required", description: "Please sign in to join the waitlist.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    const result = await submitDocumentRegisterSignup(
      email,
      user.id,
      getActiveWorkspaceId() ?? undefined,
    );
    setIsSubmitting(false);
    if (result === "error") {
      toast({ title: "Something went wrong", description: "We couldn't add you to the waitlist. Please try again.", variant: "destructive" });
      return;
    }
    setJoined(true);
    toast(
      result === "duplicate"
        ? { title: "Already on the list", description: "This email is already on the Document Register waitlist." }
        : { title: "You're on the list!", description: "We'll email you the moment Registers go live." },
    );
  };

  return (
    <RevisrLayout>
      <div className="flex-1 overflow-y-auto bg-[#F4F6F5]">
        <div className="max-w-[1280px] mx-auto px-8 py-7 pb-16">

          {/* Page header */}
          <div className="flex items-start justify-between gap-6 mb-5">
            <div className="flex items-start gap-3.5">
              <div
                className="w-1 h-9 rounded-full flex-shrink-0 mt-0.5"
                style={{ background: "linear-gradient(180deg,#fbbf24,#f59e0b)" }}
              />
              <div>
                <h1 className="text-[26px] font-extrabold text-gray-900 tracking-tight flex items-center gap-2.5 leading-none">
                  Document Register
                  <span
                    className="pro-badge text-[10px] font-extrabold px-2 py-0.5 rounded"
                    style={{
                      background: "linear-gradient(135deg,rgba(251,191,36,.18),rgba(245,158,11,.18))",
                      
                      border: "1px solid rgba(245,158,11,.35)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    PRO · COMING SOON
                  </span>
                </h1>
                <p className="text-sm text-gray-500 mt-1.5 leading-relaxed max-w-2xl">
                  A live, audit-ready inventory of every hazard, control and residual risk across your organisation — with the same register experience previewed below applied to your risks.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <button
                className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-[13px] font-bold text-[#1f1306] transition-all"
                style={{
                  background: "linear-gradient(135deg,#F59E0B,#fbbf24)",
                  boxShadow: "0 8px 20px -8px rgba(245,158,11,.55)",
                }}
              >
                <Zap className="h-3.5 w-3.5" /> Join Pro waitlist
              </button>
            </div>
          </div>

          {/* Notify section Ã¢â‚¬â€ top */}
          <div
            className="pro-notify rounded-xl border border-[#1AB790]/30 p-4 mb-5 flex items-center justify-between gap-5 flex-wrap"
          >
            <div>
              <h4 className="text-[14px] font-semibold text-gray-800 mb-0.5">Be the first to know when Registers go live.</h4>
              <p className="text-[12.5px] text-gray-500">
                We'll email you the moment Document Register and the other Pro registers are available on your workspace.
              </p>
            </div>
            {joined ? (
              <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1AB790] flex-shrink-0">
                <Bell className="h-4 w-4" /> You're on the list — we'll be in touch.
              </div>
            ) : (
              <form
                className="flex items-center gap-2.5 flex-shrink-0"
                onSubmit={handleNotifySubmit}
              >
                <input
                  type="email"
                  placeholder="you@organisation.com"
                  required
                  value={email}
                  onChange={(e) => { setEmailTouched(true); setEmail(e.target.value); }}
                  disabled={isSubmitting}
                  className="w-60 px-3.5 py-2 rounded-lg border border-gray-300 text-[13px] outline-none focus:border-[#1AB790] focus:ring-2 focus:ring-[#1AB790]/20 transition-all disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-bold text-white transition-colors flex-shrink-0 disabled:opacity-60"
                  style={{ background: "#1AB790", boxShadow: "0 4px 12px -4px rgba(26,183,144,.5)" }}
                >
                  {isSubmitting ? "Joining…" : "Notify me"} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </form>
            )}
          </div>
          {/* Coming soon banner */}
          <div
            className="pro-callout rounded-xl border p-4 mb-4 flex items-center justify-between gap-5 flex-wrap"
          >
            <div className="flex items-center gap-3.5">
              <div
                className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0"
                style={{
                  background: "linear-gradient(135deg,#FBBF24,#F59E0B)",
                  boxShadow: "0 2px 6px -2px rgba(245,158,11,.5)",
                }}
              >
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold text-[#78400D] flex items-center gap-2 flex-wrap leading-snug">
                  <span
                    className="pro-badge inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-widest"
                    style={{
                      background: "rgba(245,158,11,.15)",
                      border: "1px solid rgba(245,158,11,.30)",
                      
                    }}
                  >
                    <Sparkles className="h-2.5 w-2.5" /> Coming soon
                  </span>
                  Document Register is launching this quarter as part of Revisr Pro.
                </h2>
                <p className="text-[13px] text-[#8A5A1A] mt-1 leading-relaxed max-w-2xl">
                  This is a preview of how the Register experience will look once enabled. Sample data shown is from the{" "}
                  <strong className="text-[#5A3A0A]">Internal Document Register (ISO 9001)</strong> — every register on your workspace (Risk, SDS, Training, OFI, Statutory) will share this same toolbar, table and actions on launch day.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-[#8A5A1A]">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{
                  background: "#F59E0B",
                  boxShadow: "0 0 0 3px rgba(245,158,11,.25)",
                  animation: "pulse 2s ease-in-out infinite",
                }}
              />
              <span><strong className="text-[#5A3A0A]">14 organisations</strong> on the waitlist</span>
            </div>
          </div>

          {/* Register table */}
          <div className="bg-white border border-gray-200 rounded-[10px] overflow-hidden shadow-sm">

            {/* Toolbar */}
            <div className="flex items-center gap-0.5 px-3.5 py-2 border-b border-gray-100 flex-wrap min-h-[48px]">
              <button className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[13.5px] font-semibold text-white bg-blue-600 hover:bg-blue-700 mr-1.5 transition-colors">
                <Plus className="h-3.5 w-3.5" /> Add new item
              </button>
              {[
                { icon: History, label: "Undo", disabled: true },
                null,
                { icon: Download, label: "Export" },
                { icon: Link2, label: "Copy link" },
              ].map((btn, i) =>
                btn === null ? (
                  <div key={i} className="w-px h-5 bg-gray-200 mx-1" />
                ) : (
                  <button
                    key={i}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                      btn.disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"
                    }`}
                    disabled={btn.disabled}
                  >
                    <btn.icon className="h-3.5 w-3.5" />
                    {btn.label}
                  </button>
                )
              )}
            </div>

            {/* Table title */}
            <div className="flex items-center gap-2.5 px-6 py-4 bg-[#FAFBFC] border-b border-gray-100">
              <h3 className="text-[18px] font-semibold text-gray-800 tracking-tight">
                Internal Document Register (ISO9001)
              </h3>
              <span className="text-gray-700 text-sm cursor-pointer opacity-70 hover:opacity-100 hover:text-amber-500 transition-colors">★</span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13.5px]">
                <thead>
                  <tr>
                    {["Document Type", "Title", "Issue Date", "Document Classif…", "Link to Document"].map((h) => (
                      <th
                        key={h}
                        className="text-left px-4 py-3 text-[12.5px] font-semibold text-gray-500 bg-white border-b border-gray-100 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                    <th className="text-left px-4 py-3 text-[12.5px] font-medium text-gray-500 bg-white border-b border-gray-100">
                      <span className="inline-flex items-center gap-1 cursor-pointer hover:text-gray-800 transition-colors">
                        <Plus className="h-3 w-3" /> Add column
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {REGISTER_ROWS.map((row, i) => (
                    <tr key={i} className="group hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-[18px] text-gray-800 border-b border-gray-50 relative pl-7">
                        <span
                          className="absolute left-3.5 top-3 bottom-3 w-0.5 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors"
                        />
                        {row.type}
                      </td>
                      <td className="px-4 py-[18px] font-semibold text-gray-800 border-b border-gray-50 max-w-[300px]">
                        {row.title}
                      </td>
                      <td className="px-4 py-[18px] text-gray-800 border-b border-gray-50 tabular-nums">
                        {row.date}
                      </td>
                      <td className="px-4 py-[18px] border-b border-gray-50">
                        <span className="inline-flex items-center px-3.5 py-1 rounded-full text-[13px] text-gray-800 bg-white border border-gray-300 whitespace-nowrap">
                          {row.cls === "public" ? "Public Use" : "Business Use Only"}
                        </span>
                      </td>
                      <td className="px-4 py-[18px] border-b border-gray-50">
                        <span className="inline-flex items-center gap-1 text-blue-600 underline underline-offset-2 decoration-[1px] text-[13.5px] cursor-pointer hover:text-blue-800 transition-colors">
                          {row.link}
                          <ExternalLink className="h-3 w-3 opacity-60" />
                        </span>
                      </td>
                      <td className="px-4 py-[18px] border-b border-gray-50" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info chips */}
          <div className="flex gap-4 flex-wrap mt-4 text-[12.5px] text-gray-500">
            {[
              { icon: ShieldCheck, text: <>Aligned to <strong className="text-gray-700">ISO 31000 / 45001</strong></> },
              { icon: Link2, text: <>Auto-links to <strong className="text-gray-700">Your Policies</strong></> },
              { icon: History, text: "Full review & control-change audit trail" },
              { icon: Download, text: "Branded PDF & DOCX export" },
            ].map((chip, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-gray-200">
                <chip.icon className="h-3.5 w-3.5 text-[#1AB790]" />
                {chip.text}
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%,100% { box-shadow: 0 0 0 3px rgba(245,158,11,.22); }
          50% { box-shadow: 0 0 0 6px rgba(245,158,11,0); }
        }
      `}</style>
    </RevisrLayout>
  );
};





